/* Round 7 (17 Oct 2026) — keyboard order for the shared glossary explainer (GlossaryTip).
 *
 * The explainer's panel is drawn in a portal at the END of <body>, so the browser's own Tab
 * order jumps from the ⓘ to the next control on the page and the panel closes before its "more"
 * button can take focus. A keyboard user could never expand a long definition. This table is the
 * order the component applies instead:
 *
 *   ⓘ (open, "more" shown)  Tab        → "more" (the panel stays open)
 *   "more"                  Shift+Tab  → back to the ⓘ (the panel stays open)
 *   "more"                  Tab        → close, then the control after the ⓘ on the page
 *   "more"                  Escape     → close, focus back on the ⓘ
 *   ⓘ                       Escape     → close, focus stays on the ⓘ
 *   anything else                      → the browser's own behaviour (Tab from an ⓘ with no
 *                                        "more" moves on and closes, as before)
 *
 * PURE: no DOM, so the Node test runner can pin the order.
 */

export type TipFocusAt = 'trigger' | 'more';

export type TipKeyAction =
  | 'native'            // let the browser handle the key
  | 'focus-more'        // prevent the default; focus the panel's "more"
  | 'focus-trigger'     // prevent the default; focus the ⓘ, the panel stays open
  | 'close-to-next'     // prevent the default; close; focus the control after the ⓘ
  | 'close-to-trigger'  // close; focus the ⓘ
  | 'close';            // close; focus stays where it is

export interface TipKeyInput {
  key: string;
  shift: boolean;
  at: TipFocusAt;
  open: boolean;
  /** The panel shows a "more" / "less" button (the definition is long). */
  hasMore: boolean;
}

export function tipKeyAction({ key, shift, at, open, hasMore }: TipKeyInput): TipKeyAction {
  if (!open) return 'native';
  if (key === 'Escape') return at === 'more' ? 'close-to-trigger' : 'close';
  if (key !== 'Tab') return 'native';
  if (at === 'trigger') return !shift && hasMore ? 'focus-more' : 'native';
  return shift ? 'focus-trigger' : 'close-to-next';
}
